import { Link } from "react-router-dom";
import { useState, useRef, useEffect } from "react";

/* ── Changelog entries (newest → oldest) ────────────────── */
const CHANGELOG = [
  {
    date: "Apr 7, 2026",
    emoji: "📈",
    title: "AI Business Audit",
    desc: "Launched standalone operational insight engine. Generates 8-page decision-ready PDF audits grounded in ABS industry data.",
  },
  {
    date: "Mar 16, 2026",
    emoji: "🔧",
    title: "Deep Scraping Engine",
    desc: "Integrated Scrapling for TLS-impersonated, anti-bot-resistant web scraping with StealthyFetcher fallback.",
  },
  {
    date: "Mar 12, 2026",
    emoji: "🔒",
    title: "Origin Security Fix",
    desc: "Corrected ALLOWED_ORIGINS namespace for production widget — fixes 403 on bot/chat.",
  },
  {
    date: "Mar 6, 2026",
    emoji: "📋",
    title: "SOP Bot",
    desc: "Launched internal employee knowledge base — second product line with file upload training.",
  },
  {
    date: "Mar 5, 2026",
    emoji: "🛡️",
    title: "Widget Security",
    desc: "Domain allowlist enforcement, Origin header validation, fail-closed unknown namespaces, security logging.",
  },
  {
    date: "Mar 5, 2026",
    emoji: "💬",
    title: "Embeddable Chat Widget",
    desc: "Universal yukti-widget.js with Shadow DOM isolation, YuktiConfig support, localStorage persistence, 6-turn history.",
  },
  {
    date: "Mar 5, 2026",
    emoji: "📊",
    title: "Intelligence Moat (Layers 1-5)",
    desc: "YAMI scoring engine, automated gap detection, ROI benchmarks database, analytics flywheel with Supabase logging.",
  },
  {
    date: "Mar 3, 2026",
    emoji: "🔍",
    title: "SEO & Performance",
    desc: "Canonical tags, Open Graph meta, FAQ schema JSON-LD, code splitting, LCP optimisation, sitemap.xml.",
  },
  {
    date: "Mar 1, 2026",
    emoji: "📝",
    title: "Blog Platform",
    desc: "Blog index page, AI automation article, ROI calculator post, expanded sitemap coverage.",
  },
  {
    date: "Mar 1, 2026",
    emoji: "🤖",
    title: "SiteBot Self-Learning",
    desc: "Auto-crawl and index websites, periodic knowledge refresh, policy-page probing, Yukti persona.",
  },
  {
    date: "Feb 27, 2026",
    emoji: "🏗️",
    title: "Structured Blueprint Engine",
    desc: "Refactored to clean intelligence/services architecture layers for automation blueprint generation.",
  },
  {
    date: "Feb 26, 2026",
    emoji: "🌐",
    title: "Enterprise Website Scanner",
    desc: "Deep multi-page crawling engine with sitemap & robots.txt parsing, table/list extraction, PDF content, traffic intelligence.",
  },
  {
    date: "Feb 22, 2026",
    emoji: "🚀",
    title: "Platform Launch",
    desc: "Home page wizard, enterprise RAG pipeline with reranker, NVIDIA embeddings, lead capture form, chatbot widget.",
  },
];

export function Footer() {
  const [changelogOpen, setChangelogOpen] = useState(false);
  const contentRef = useRef(null);
  const [contentHeight, setContentHeight] = useState(0);

  useEffect(() => {
    if (contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight);
    }
  }, [changelogOpen]);

  const footerLinks = [
    { to: "/about", label: "About" },
    { to: "/contact", label: "Contact" },
    { to: "/privacy", label: "Privacy & Cookies" },
    { to: "/terms", label: "Terms" },
    { to: "/affiliate-disclosure", label: "Affiliate Disclosure" },
  ];

  return (
    <footer data-testid="footer" className="border-t border-white/5 bg-[#09090b]">

      {/* ── Changelog Side Drawer (SaaS Style) ──────────────── */}
      
      {/* Backdrop */}
      {changelogOpen && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-[2px] z-40 animate-fade-in"
          onClick={() => setChangelogOpen(false)}
        />
      )}

      {/* Drawer Panel */}
      <div 
        className={`fixed top-0 right-0 h-full w-full max-w-md bg-[#09090b] border-l border-white/10 z-50 transform transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] shadow-2xl flex flex-col ${
          changelogOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Header */}
        <div className="px-6 py-5 border-b border-white/10 flex items-center justify-between shrink-0 bg-[#09090b]">
          <h3 className="text-white text-[15px] font-medium tracking-wide flex items-center gap-2">
            <svg className="w-4 h-4 text-zinc-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Changelog
          </h3>
          <button 
            onClick={() => setChangelogOpen(false)} 
            className="text-zinc-500 hover:text-white transition-colors p-1"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Scrollable Timeline Content */}
        <div className="flex-1 overflow-y-auto p-6 scrollbar-hide">
          <div className="relative pl-5 border-l border-white/[0.06]">
            {CHANGELOG.map((entry, i) => (
              <div
                key={i}
                className="relative mb-6 last:mb-2 group"
              >
                {/* Dot on timeline */}
                <div className="absolute -left-[22.5px] top-[6px] w-[8px] h-[8px] rounded-full border border-white/10 bg-[#18181b] group-hover:border-white/30 group-hover:bg-white/10 transition-colors duration-200" />

                {/* Date */}
                <p className="text-[11px] font-mono text-zinc-500 tracking-wider mb-1 uppercase">
                  {entry.date}
                </p>

                {/* Title */}
                <p className="text-[14px] text-zinc-100 font-medium leading-snug mb-1">
                  {entry.emoji}{" "}
                  <span>{entry.title}</span>
                </p>

                {/* Description */}
                <p className="text-[12px] text-zinc-400 leading-relaxed">
                  {entry.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Original nav + copyright bar ─────────────────── */}
      <div className="border-t border-white/5">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between gap-6">

          {/* Nav links — hidden on very small screens */}
          <nav className="hidden sm:flex items-center gap-5 flex-wrap">
            {footerLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                data-testid={`footer-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="text-xs font-normal text-zinc-600 hover:text-zinc-400 transition-colors duration-150"
              >
                {link.label}
              </Link>
            ))}
            
            <button
              id="changelog-toggle"
              onClick={() => setChangelogOpen(true)}
              className="text-xs font-normal text-zinc-600 hover:text-zinc-400 transition-colors duration-150 flex items-center gap-1 cursor-pointer select-none bg-transparent border-none p-0"
            >
              View Changelog
            </button>
          </nav>

          {/* Copyright */}
          <p className="text-xs text-zinc-700 shrink-0">
            © 2026 Yukti.
          </p>
        </div>
      </div>
    </footer>
  );
}
